import moment from 'moment';
import { Country, Region, Site, Subdivision } from '@esg/esg-global-types';
import { MetadataError } from '@ep/error-handling';
import { getRegions } from '../lib/metric_capture/region';
import { getSites } from '../lib/metric_capture/site';
import { getSubdivisions } from '../lib/metric_capture/subdivision';
import { generateUuidV4 } from './uuid';
import { FUNCTION_UNKNOWN_FAILURE } from './global_variables';

/**
 * Converts an array of objects into a CSV-formatted string.
 * @param {Array<T>} data An array of objects to convert into CSV format.
 * @returns {string} A CSV-formatted string representing the data. Returns an empty string if the input is empty.
 */
const convertToCsv = <T extends Record<string, unknown>>(data: Array<T>): string => {
  if (!data.length) return '';

  const headers: Array<keyof T> = Object.keys(data[0]);
  const csv_rows: Array<string> = data.map((row: T) =>
    headers.map((header: keyof T) => JSON.stringify(row[header] ?? '')).join(',')
  );

  return `${headers.join(',')}\n${csv_rows.join('\n')}`;
};

/**
 * Triggers a download of a CSV file in the browser.
 * @param {string} file_name The desired name of the downloaded file (e.g., "data.csv").
 * @param {string} csv_content The CSV content to be included in the file.
 * @returns {void}
 */
const downloadCsv = (file_name: string, csv_content: string): void => {
  const blob: Blob = new Blob([csv_content], { type: 'text/csv;charset=utf-8;' });
  const url: string = URL.createObjectURL(blob);
  const link: HTMLAnchorElement = document.createElement('a');

  link.href = url;
  link.download = file_name;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Downloads CSV exports of regions, subdivisions, and sites, including a sample import template.
 * @param {string} group_id The group ID to fetch and associate data with.
 * @param {string} company_id The company ID to fetch and associate data with.
 * @returns {Promise<void>} A promise that resolves when all files are downloaded.
 */
export const downloadImportSitesFromCsvTemplate = async (
  group_id: string,
  company_id: string
): Promise<void> => {
  try {
    const [regions, subdivisions, sites]: [Array<Region>, Array<Subdivision>, Array<Site>] =
      await Promise.all([
        getRegions(false, group_id, company_id),
        getSubdivisions(group_id, company_id),
        getSites(group_id, company_id)
      ]);

    const base_import_template_keys: Record<string, string> = {
      group_id: group_id,
      company_id: company_id
    };

    const regions_csv: string = convertToCsv(
      regions.map((region: Region) => {
        return {
          ...base_import_template_keys,
          region_id: region.id,
          region_name: region.name,
          country_name: (region.country as Country).name,
          continent_name: (region.country as Country).continent
        };
      })
    );

    const subdivisions_csv: string = convertToCsv(
      subdivisions.map((subdivision: Subdivision) => {
        return {
          ...base_import_template_keys,
          subdivision_id: subdivision.id,
          subdivision_name: subdivision.name
        };
      })
    );

    const sites_csv: string = convertToCsv(
      sites.map((site: Site) => {
        return {
          ...base_import_template_keys,
          site_name: site.name,
          subdivision_id: site.subdivision.id,
          region_id: site.region.id,
          longitude: site.longitude,
          latitude: site.latitude,
          glo_id: site.glo_id
        };
      })
    );

    const sites_import_csv: string = convertToCsv([
      {
        site_name: 'Sample Site Name',
        subdivision_id: 'E9o51EdfUKSZETZpjFDv',
        region_id: 'ZA_WSTRNCP',
        latitude: '-33.8716926',
        longitude: '18.6400165',
        glo_id: 'sample_glo_id'
      }
    ]);

    const tstamp: number = moment().unix();

    downloadCsv(`${group_id}_${company_id}_regions_${tstamp}.csv`, regions_csv);
    downloadCsv(`${group_id}_${company_id}_subdivisions_${tstamp}.csv`, subdivisions_csv);
    downloadCsv(`${group_id}_${company_id}_sites_${tstamp}.csv`, sites_csv);
    downloadCsv(`${group_id}_${company_id}_sites_import_${tstamp}.csv`, sites_import_csv);
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : FUNCTION_UNKNOWN_FAILURE('lib/util/csv.ts', 'downloadImportSitesFromCsvTemplate'),
      {
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Creates a name for a CSV file using a provided entity string
 * @param {string} entity_label The entity to use to create the name.
 * @returns {string} The name in the format `Syntiro_entity_label_timestamp`
 */
export const createCSVFileNameForEntity = (entity_label: string): string => {
  let file_name = 'Syntiro';
  const timestamp: string = new Date().toISOString();

  if (entity_label === 'Resources') {
    file_name += `_Resouce_Centre_${timestamp}`;
  } else {
    file_name += `_${entity_label}_${timestamp}`;
  }
  return file_name;
};
